import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import {
  faArrowsRotate,
  faCircleCheck,
  faCircleXmark,
  faCopy,
  faEllipsisVertical,
  faEye,
  faPaperclip,
  faPenToSquare,
  faPrint,
  faSquareMinus,
  faTag,
  faTruck,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@shared/components/ui/button';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OrdersApiAdapter } from '@modules/Orders/List/adapters/OrdersApiAdapter';
import {
  RefreshLogisticsResultModal,
  type RefreshLogisticsResultModalRef,
} from '@modules/Orders/List/components/modals';
import {
  OrderStatus,
  getDropdownActionsByStatus,
  getStatusForDropdown,
  injectAttachmentsDropdownAction,
  injectRefreshLogisticsDropdownAction,
} from '@modules/Orders/List/config/orderStatusConfig';
import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { AttachmentsModal } from '../modals/AttachmentsModal';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { OrderDuplicationAdapter } from '@shared/adapters/OrderDuplicationAdapter';
import { apiClientWH } from '@shared/services';
import { DuplicateOrderService } from '@shared/services/DuplicateOrderService';
import { ShippedPaymentModal } from '../modals/ShippedPaymentModal';
import { ShipmentStatusCorrectionModal } from '../modals/ShipmentStatusCorrectionModal';
import type { Order } from '../../models/Order';

interface DropdownActionsProps {
  order: Order | null;
  onActionSuccess: () => void;
}

export const DropdownActions: FC<DropdownActionsProps> = ({ order, onActionSuccess }) => {
  const { t } = useTranslation(['viewOrdersModule', 'ordersModule']);
  const navigate = useNavigate();
  const confirmModalRef = useRef<ConfirmModalRef>(null);
  const refreshLogisticsResultModalRef = useRef<RefreshLogisticsResultModalRef>(null);
  const shippedPaymentModalRef = useRef<ModalCenterRef>(null);
  const shipmentStatusCorrectionModalRef = useRef<ModalCenterRef>(null);
  const attachmentsModalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();
  const { hasPermission, canAccess } = usePermissions();
  const api = new OrdersApiAdapter(createHttpClient(apiClientWH));

  // 🎯 Verificar permisos de pedidos
  const canConfirm = hasPermission('orders.confirm');
  const canCancel = hasPermission('orders.cancel');
  const canUncancel = hasPermission('orders.uncancel');
  const canManageShipmentStatusCorrection =
    canAccess(['tenant_3pl']) && hasPermission('orders.shipment-status-correction.manage');
  const isTenant3pl = canAccess(['tenant_3pl']);

  if (!order) {
    return null;
  }

  const status = getStatusForDropdown(order.status.id);

  const getActions = (currentStatus: OrderStatus) => {
    const allActions = getDropdownActionsByStatus(currentStatus, (key: string) => t(key, { ns: 'ordersModule' }), {
      faCircleXmark,
      faArrowsRotate,
      faCircleCheck,
      faCopy,
      faSquareMinus,
      faEye,
      faTag,
      faPrint,
      faXmark,
    });
    // Excluimos la acción de 'ver pedido' porque ya estamos en esa vista
    const filteredActions = allActions.filter((a) => a.action !== 'view_order');

    // 🎯 Filtrar acciones según permisos
    return filteredActions.filter((action) => {
      // Cancelar requiere orders.cancel
      if (action.action === 'cancel') return canCancel;

      // Marcar para envío requiere orders.confirm
      if (action.action === 'mark_send') return canConfirm;

      // Descancelar requiere orders.uncancel
      if (action.action === 'uncancel') return canUncancel;

      // Otras acciones (duplicate, update_store, labels, unassign_stock) no requieren permisos específicos
      return true;
    });
  };

  const baseActions = injectAttachmentsDropdownAction(
    injectRefreshLogisticsDropdownAction(getActions(status), {
      enabled: canAccess(['tenant_3pl']),
      status,
      t: (key: string) => t(key, { ns: 'ordersModule' }),
      icon: faArrowsRotate,
      insertMode: 'prepend',
    }),
    {
      enabled: (order.attachments?.length ?? 0) > 0,
      t: (key: string) => t(key, { ns: 'ordersModule' }),
      icon: faPaperclip,
      insertMode: 'prepend',
    },
  );

  const actions = (() => {
    if (!isTenant3pl || (status !== 'enviado' && status !== 'devuelto')) return baseActions;

    const shippedActions = [...baseActions];

    shippedActions.push({
      id: 'edit_shipped_payment',
      icon: faPenToSquare,
      label: t('actions.edit-shipped-payment'),
      action: 'edit_shipped_payment',
      requiresConfirmation: false,
    });

    if (canManageShipmentStatusCorrection) {
      shippedActions.push({
        id: 'edit_shipment_status',
        icon: faTruck,
        label: t('actions.edit-shipment-status'),
        action: 'edit_shipment_status',
        requiresConfirmation: false,
      });
    }

    return shippedActions;
  })();

  const isActionDisabled = (actionId: string): boolean => {
    const disabledActions = [
      'print_labels',
      // 'update_store', // Ya no está deshabilitada
      // 'duplicate', // Ya no está deshabilitada
    ];
    return disabledActions.includes(actionId);
  };

  const handlePrintLabel = async () => {
    if (!order?.id) {
      toast({
        title: t('toast.print_error.title', 'Error'),
        description: 'ID de orden no disponible',
        variant: 'destructive',
      });
      return;
    }

    const printingMethod = 'pdf';

    try {
      const response = await api.printOrder(order.id, printingMethod);

      let linkUrl = null;

      if (typeof response === 'string') {
        linkUrl = response;
      } else if (Array.isArray(response) && response.length > 0) {
        linkUrl = response[0];
      } else if (response && response.link) {
        linkUrl = response.link;
      }

      if (linkUrl) {
        toast({
          title: t('toast.print_success.title', 'Etiqueta generada'),
          description: t(
            'toast.print_success.description',
            'La etiqueta fue generada y será redirigida en unos segundos',
          ),
          variant: 'default',
        });

        setTimeout(() => {
          const finalUrl = String(linkUrl).trim();

          if (finalUrl && finalUrl.startsWith('http')) {
            window.open(finalUrl, '_blank');
          } else {
            toast({
              title: t('toast.print_error.title', 'Error'),
              description: 'URL de la etiqueta no es válida',
              variant: 'destructive',
            });
          }
        }, 100);
      } else {
        toast({
          title: t('toast.print_error.title', 'Error'),
          description: 'No se recibió el link de la etiqueta',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('toast.print_error.description', 'Error al generar la etiqueta');

      toast({
        title: t('toast.print_error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleDuplicateOrder = async () => {
    try {
      // Convertir la orden actual al formato para duplicación
      const duplicateData = await OrderDuplicationAdapter.adaptOrderForDuplication(order);

      // Generar una referencia única
      duplicateData.orderInfo.reference = OrderDuplicationAdapter.generateDuplicateReference(order.ref);

      // Guardar en localStorage
      DuplicateOrderService.saveDuplicateData(duplicateData);

      // Mostrar toast de éxito
      toast({
        title: 'Datos preparados',
        description: 'Los datos han sido cargados para crear una nueva orden',
        variant: 'default',
      });

      // Navegar a crear orden
      navigate('/orders/create');
    } catch (error) {
      console.error('Error duplicating order:', error);
      toast({
        title: t('actions.duplicate.error.title', { ns: 'ordersModule' }),
        description: t('actions.duplicate.error.description', {
          ns: 'ordersModule',
        }),
        variant: 'destructive',
      });
    }
  };

  const handleRefreshLogistics = () => {
    if (!order?.id) {
      toast({
        title: t('toast.error.title', { ns: 'ordersModule' }),
        description: 'ID de orden no disponible',
        variant: 'destructive',
      });
      return;
    }
    refreshLogisticsResultModalRef.current?.open({
      orderId: order.id,
      onClose: onActionSuccess,
    });
  };

  const handleViewAttachments = () => {
    if (!order.attachments?.length) {
      return;
    }
    attachmentsModalRef.current?.openModal();
  };

  const handleAction = async (action: any) => {
    if (isActionDisabled(action.action)) {
      return;
    }

    // Manejar duplicación de orden
    if (action.action === 'duplicate') {
      handleDuplicateOrder();
      return;
    }

    // Manejar ver etiqueta
    if (action.action === 'labels') {
      await handlePrintLabel();
      return;
    }

    if (action.action === 'refresh_logistics') {
      handleRefreshLogistics();
      return;
    }

    if (action.action === 'edit_shipped_payment') {
      shippedPaymentModalRef.current?.openModal();
      return;
    }

    if (action.action === 'edit_shipment_status') {
      shipmentStatusCorrectionModalRef.current?.openModal();
      return;
    }

    if (action.action === 'view_attachments') {
      handleViewAttachments();
      return;
    }

    // Manejar actualizar desde tienda
    if (action.action === 'update_store') {
      if (!order?.id) {
        toast({
          title: t('toast.error.title', { ns: 'ordersModule' }),
          description: 'ID de orden no disponible',
          variant: 'destructive',
        });
        return;
      }

      // Función helper para obtener el nombre del canal
      const getChannelName = (channelId?: number): string => {
        switch (channelId) {
          case 1:
            return 'Shopify';
          case 3:
            return 'WooCommerce';
          default:
            return t('confirmModals.updateFromStore.defaultChannel', {
              ns: 'ordersModule',
              defaultValue: 'la tienda',
            });
        }
      };

      const channelName = getChannelName(order.shop?.channel_id);
      const title = t('confirmModals.updateFromStore.title', {
        ns: 'ordersModule',
      });
      const message = t('confirmModals.updateFromStore.message', {
        orderNumber: order.ref || 'N/A',
        channelName,
        ns: 'ordersModule',
      });

      confirmModalRef.current?.open(
        async () => {
          try {
            await api.updateFromShop(order.id);
            toast({
              title: t('toast.update_store_success.title', {
                ns: 'ordersModule',
              }),
              description: t('toast.update_store_success.description', {
                orderNumber: order.ref,
                ns: 'ordersModule',
              }),
              variant: 'success',
            });
            onActionSuccess();
          } catch (error) {
            const apiMessage =
              (error as any)?.response?.data?.error?.message ||
              (error as any)?.response?.data?.message ||
              (error as any)?.message ||
              t('toast.error.description', { ns: 'ordersModule' });
            toast({
              title: t('toast.error.title', { ns: 'ordersModule' }),
              description: apiMessage,
              variant: 'destructive',
            });
          }
        },
        undefined,
        title,
        message,
        t('buttons.confirm', { ns: 'ordersModule' }),
        t('buttons.cancel', { ns: 'ordersModule' }),
      );
      return;
    }

    if (action.requiresConfirmation) {
      const confirmationKey = action.confirmationKey;
      const title = t(`confirmModals.${confirmationKey}.title`, {
        ns: 'ordersModule',
      });
      const message = t(`confirmModals.${confirmationKey}.message`, {
        orderNumber: order?.ref || 'N/A',
        ns: 'ordersModule',
      });

      confirmModalRef.current?.open(
        async () => {
          try {
            switch (action.action) {
              case 'cancel':
                await api.cancelOrder(order.id);
                toast({
                  title: t('toast.cancel_success.title', {
                    ns: 'ordersModule',
                  }),
                  description: t('toast.cancel_success.description', {
                    orderNumber: order.ref,
                    ns: 'ordersModule',
                  }),
                  variant: 'success',
                });
                break;
              case 'uncancel':
                await api.uncancelOrder(order.id);
                toast({
                  title: t('toast.uncancel_success.title', {
                    ns: 'ordersModule',
                  }),
                  description: t('toast.uncancel_success.description', {
                    orderNumber: order.ref,
                    ns: 'ordersModule',
                  }),
                  variant: 'success',
                });
                break;
              case 'mark_send':
                await api.confirmOrder(order.id);
                toast({
                  title: t('toast.mark_send_success.title', {
                    ns: 'ordersModule',
                  }),
                  description: t('toast.mark_send_success.description', {
                    orderNumber: order.ref,
                    ns: 'ordersModule',
                  }),
                  variant: 'success',
                });
                break;
              case 'unassign_stock':
                await api.unassignStock(order.id);
                toast({
                  title: t('toast.unassign_stock_success.title', {
                    ns: 'ordersModule',
                  }),
                  description: t('toast.unassign_stock_success.description', {
                    orderNumber: order.ref,
                    ns: 'ordersModule',
                  }),
                  variant: 'success',
                });
                break;
              default:
                return;
            }
            onActionSuccess();
          } catch (error) {
            const apiMessage =
              (error as any)?.response?.data?.error?.message ||
              (error as any)?.message ||
              t('toast.error.description', { ns: 'ordersModule' });
            toast({
              title: t('toast.error.title', { ns: 'ordersModule' }),
              description: apiMessage,
              variant: 'destructive',
            });
          }
        },
        undefined,
        title,
        message,
        t('buttons.confirm', { ns: 'ordersModule' }),
        t('buttons.cancel', { ns: 'ordersModule' }),
      );
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon">
            <FontAwesomeIcon icon={faEllipsisVertical} className="!w-3 !h-3" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-52 border-card-border">
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.id}
              onClick={() => handleAction(action)}
              className={`whitespace-nowrap ${isActionDisabled(action.action) ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isActionDisabled(action.action)}
            >
              <FontAwesomeIcon icon={action.icon} className="mr-2 w-3.5" />
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmModal ref={confirmModalRef} />
      <RefreshLogisticsResultModal ref={refreshLogisticsResultModalRef} />
      <ShippedPaymentModal
        ref={shippedPaymentModalRef}
        orderId={order?.id}
        currentTotal={order?.total}
        currentPaymentMethodId={order?.payment_method_id}
        onSuccess={onActionSuccess}
      />
      {isTenant3pl && (
        <ShipmentStatusCorrectionModal
          ref={shipmentStatusCorrectionModalRef}
          orderId={order?.id}
          currentShipmentStatusId={order?.shipment?.shipment_status_id ?? order?.shipment?.shipment_status?.id}
          onSuccess={onActionSuccess}
        />
      )}
      <AttachmentsModal ref={attachmentsModalRef} attachments={order.attachments ?? []} />
    </>
  );
};
